import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Logger } from 'src/services/core/logger/logger.service';
import { Meal, AddOn } from 'src/services/classes/models/order-item';
import { RestaurantService } from 'src/services/data/restaurant/restaurant.service';
import { NotificationsService } from 'src/services/classes/notifications/notifications.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HandleSelfOrderComponent } from 'src/app/shared/handle-self-order/handle-self-order.component';
import { LoaderService } from 'src/services/classes/loader/loader.service';
import { Response } from 'src/services/classes/models/response';
import { untilDestroyed } from 'src/services/core/until-destroyed/until-destroyed';
import { finalize } from 'rxjs/operators';

const log = new Logger();
@Component({
  selector: 'app-order-successful',
  templateUrl: './order-successful.component.html',
  styleUrls: ['./order-successful.component.scss'],
})
export class OrderSuccessfulComponent implements OnInit, OnDestroy {
  cartTotal = 0;
  cart: Map<string, Meal[]> = new Map();
  specialInstructions: string | null = null;
  isSuccessPage = true;
  navMenuIsOpen = false;
  openNavMenu = '/assets/icons/open-nav-menu.svg';
  closeNavMenu = '/assets/icons/close-nav-menu.svg';
  orderType: string | null = null;

  constructor(
    private router: Router,
    private restaurantService: RestaurantService,
    private notifyService: NotificationsService,
    private modalService: NgbModal,
    private loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    this.getCartInfo();
    this.orderType = localStorage.getItem('order-type');
  }

  goToCheckout() {
    this.router.navigateByUrl('/checkout');
  }

  getCartInfo() {
    const rawOrderData = localStorage.getItem('orderData');
    const orderData = rawOrderData ? JSON.parse(rawOrderData) : {};
    try {
      // Group items by their original dishName so food and drinks placed in the
      // same selection stay together, even though the server splits them across
      // multiple orders (kitchen vs bar assignments).
      const grouped = new Map<string, any[]>();

      const collectDishes = (dishes: any) => {
        if (!dishes || typeof dishes !== 'object') return;
        Object.values(dishes).forEach((meals: any) => {
          if (!Array.isArray(meals)) return;
          meals.forEach((meal: any) => {
            const key = meal.dishName || 'Dish 1';
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key)!.push(meal);
          });
        });
      };

      if (orderData.orders && Array.isArray(orderData.orders) && orderData.orders.length > 0) {
        orderData.orders.forEach((order: any) => {
          if (order.specialRequest && !this.specialInstructions) {
            this.specialInstructions = order.specialRequest;
          }
          collectDishes(order.dishes);
        });
      } else {
        if (orderData.specialRequest) {
          this.specialInstructions = orderData.specialRequest;
        }
        collectDishes(orderData.dishes);
      }

      grouped.forEach((meals, key) => {
        const refined = this.refineOrderValue(meals);
        this.getCartTotal(refined);
        this.cart.set(key, refined);
      });

      this.notifyService.publishMessages(
        'Ready to make payment? Tap the checkout button!',
        'info',
        1
      );
    } catch (error) {
      log.debug('generating cart issue: ', error);
    }
  }

  refineOrderValue(dishContent: any[]): Meal[] | [] {
    if (dishContent.length > 0) {
      dishContent.forEach((dishItem: any) => {
        if (dishItem.addons) {
          dishItem['addOns'] = dishItem.addons;
        }

        if (dishItem.quantity) {
          dishItem['quantityOrdered'] = dishItem.quantity;
        }

        if (dishItem.unitPrice) {
          dishItem['price'] = dishItem.unitPrice;
        }
      });
    }

    return dishContent;
  }

  getCartTotal(dishContent: Meal[]) {
    dishContent.forEach((element: Meal) => {
      this.cartTotal =
        this.cartTotal + +element.price * (element.quantityOrdered! || 1);

      if (element.addOns) {
        element.addOns.forEach((addOn: AddOn) => {
          if (addOn.quantityOrdered) {
            this.cartTotal += +addOn.price * +addOn.quantityOrdered;
          } else {
            this.cartTotal += +addOn.price;
          }
        });
      }
    });
  }

  handleCartRefresh(event: string) {
    log.debug('ev: ', event);
    if (event === 'refresh-cart') {
      setTimeout(() => this.getCartInfo(), 2000);
    }
  }

  goHome() {
    const restaurantInfo = this.restaurantService.fetchRestaurantInformation();
    this.router.navigateByUrl(`/${restaurantInfo?.branchId}/dishes`);
  }

  callWaiter() {
    const currentTable = Number(localStorage.getItem('table-number')) || null;
    if (currentTable !== null) {
      this.callTheWaiterViaAPI(currentTable);
      return;
    }

    // If table is missing, ask user to pick table first, then notify waiter.
    const modalRef = this.modalService.open(HandleSelfOrderComponent, {
      size: 'lg',
      backdrop: 'static',
      animation: true,
      centered: true,
    });
    modalRef.componentInstance.source = 'from-dishes';
    modalRef.result
      .then((result) => {
        if (result && result !== 'closed modal') {
          localStorage.setItem('table-number', result);
          this.callTheWaiterViaAPI(Number(result));
        }
      })
      .catch((error) => log.debug('error from call waiter modal: ', error));
  }

  mergeTableBills() {
    const currentTable = Number(localStorage.getItem('table-number')) || null;
    if (currentTable !== null) {
      this.notifyWaiterAndNavigate(currentTable, {
        notificationType: 'merge-table-bill',
      });
      return;
    }

    const modalRef = this.modalService.open(HandleSelfOrderComponent, {
      size: 'lg',
      backdrop: 'static',
      animation: true,
      centered: true,
    });
    modalRef.componentInstance.source = 'from-dishes';
    modalRef.result
      .then((result) => {
        if (result && result !== 'closed modal') {
          localStorage.setItem('table-number', result);
          this.notifyWaiterAndNavigate(Number(result), {
            notificationType: 'merge-table-bill',
          });
        }
      })
      .catch((error) => log.debug('error from merge table modal: ', error));
  }

  groupSplitBill() {
    const currentTable = Number(localStorage.getItem('table-number')) || null;
    if (currentTable !== null) {
      this.notifyWaiterAndNavigate(currentTable, {
        notificationType: 'split-group-bill',
      });
      return;
    }

    const modalRef = this.modalService.open(HandleSelfOrderComponent, {
      size: 'lg',
      backdrop: 'static',
      animation: true,
      centered: true,
    });
    modalRef.componentInstance.source = 'from-dishes';
    modalRef.result
      .then((result) => {
        if (result && result !== 'closed modal') {
          localStorage.setItem('table-number', result);
          this.notifyWaiterAndNavigate(Number(result), {
            notificationType: 'split-group-bill',
          });
        }
      })
      .catch((error) => log.debug('error from split group modal: ', error));
  }

  changeTable() {
    const modalRef = this.modalService.open(HandleSelfOrderComponent, {
      size: 'lg',
      backdrop: 'static',
      animation: true,
      centered: true,
    });
    modalRef.componentInstance.source = 'from-dishes';

    modalRef.result
      .then((result) => {
        if (result && result !== 'closed modal') {
          localStorage.setItem('table-number', result);
        }
      })
      .catch((error) => log.debug('error from action modal: ', error));
  }

  callTheWaiterViaAPI(tableId: number) {
    this.loaderService.show();
    const payload = {
      notificationType: 'move-table',
    };
    const callTheWaiter$ = this.restaurantService.notifyWaiterForMoveTable(
      tableId,
      payload
    );
    callTheWaiter$
      .pipe(
        untilDestroyed(this),
        finalize(() => this.loaderService.hide())
      )
      .subscribe(
      (res: Response) => {
        if (res.status.toLowerCase() !== 'error') {
          res.message
            ? this.notifyService.publishMessages(res.message, 'success', 1)
            : this.notifyService.publishMessages(
                'Waiter has been notified',
                'success',
                1
              );
        } else {
          this.notifyService.publishMessages(res.message, 'danger', 1);
        }
      },
      () => undefined
    );
  }

  private notifyWaiterAndNavigate(tableId: number, payload: any) {
    this.loaderService.show();
    const notify$ = this.restaurantService.notifyWaiterForMoveTable(
      tableId,
      payload
    );
    notify$
      .pipe(
        untilDestroyed(this),
        finalize(() => this.loaderService.hide())
      )
      .subscribe(
        (res: Response) => {
          const status = (res as any)?.status?.toLowerCase?.();
          if (status !== 'error') {
            this.notifyService.publishMessages(
              (res as any)?.message || 'Waiter has been notified',
              'success',
              1
            );
            localStorage.setItem(
              'payment-notification-payload',
              JSON.stringify(payload)
            );
            this.router.navigate(['/awaiting-payment'], {
              queryParams: { mode: 'cash' },
            });
          } else {
            this.notifyService.publishMessages((res as any)?.message, 'danger', 1);
          }
        },
        () => undefined
      );
  }

  isOrderPlaced(): boolean {
    return localStorage.getItem('order-placed') !== null;
  }

  ngOnDestroy(): void {}
}
